/**
 * PIFster Modal Router
 *
 * Single source of truth for modal routing decisions.
 * Determines which modal to show and in what mode based on user state.
 *
 * Outputs FluentCart-native modes ('vote_plus_donate' | 'donate_only')
 * so consumers never need to translate router-specific mode strings.
 */

import { isLoggedIn } from './shared/auth-state';
import { resolveApiBase } from './shared/api';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

export const USER_STATE = Object.freeze({
  LOGGED_OUT: 'logged_out',
  INELIGIBLE: 'logged_in_ineligible',
  ELIGIBLE: 'logged_in_eligible',
  ALREADY_VOTED: 'logged_in_voted',
} as const);

export type UserState = (typeof USER_STATE)[keyof typeof USER_STATE];

export const MODAL_MODE = Object.freeze({
  MAGIC_LINK: 'magic_link',
  VOTE_PLUS_DONATE: 'vote_plus_donate',
  DONATE_ONLY: 'donate_only',
} as const);

export type ModalMode = (typeof MODAL_MODE)[keyof typeof MODAL_MODE];

export const ENTRY_POINT = Object.freeze({
  VOTE_BUTTON: 'vote_button',
  DONATE_BUTTON: 'donate_button',
  URL_INTENT: 'url_intent',
  COMPLETE_VOTE: 'complete_vote',
} as const);

export type EntryPoint = (typeof ENTRY_POINT)[keyof typeof ENTRY_POINT];

const BLOCKED_PATHS = ['/return-status', '/checkout', '/cart'] as const;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface UrlContext {
  readonly charityId: number;
  readonly periodId: number | null;
  readonly intentToken: string | null;
  readonly action: string | null;
  readonly entry: string | null;
}

export interface MintIntentResponse {
  readonly intent_token: string;
  readonly eligible: boolean;
  readonly already_voted: boolean;
  readonly voted_charity_id: number | null;
  readonly cumulative_bonus_votes: number | null;
  readonly has_recurring: boolean;
  readonly period_id: number | null;
  readonly charity_id: number;
}

export interface ModalContext {
  readonly charityId: number | null;
  readonly charityName: string;
  readonly periodId: number | null;
  readonly intentToken: string | null;
  readonly eligible: boolean;
  readonly alreadyVoted: boolean;
  readonly votedCharityId: number | null;
  readonly cumulativeBonusVotes: number;
  readonly hasRecurring: boolean;
  readonly userState: UserState;
  readonly mode: ModalMode;
  readonly entryPoint: EntryPoint;
  readonly beneficiary: string | null;
  readonly donateHrefFallback: string;
  readonly bonusRatio?: number | undefined;
  readonly bonusCap?: number | undefined;
}

export interface MagicLinkContext {
  readonly charityId: number | null;
  readonly charityName: string;
  readonly periodId: number | null;
}

export interface RouteCallbacks {
  readonly openDonationModal: (context: ModalContext) => void | Promise<void>;
  readonly openMagicLinkModal: (context: MagicLinkContext) => void;
  readonly redirectToReturnStatus?: (context: ModalContext) => void;
}

interface BaseContext {
  readonly charityId: number | null;
  readonly charityName?: string | undefined;
  readonly periodId?: number | null | undefined;
  readonly intentToken?: string | null | undefined;
  readonly beneficiary?: string | null | undefined;
  readonly donateHrefFallback?: string | undefined;
  readonly bonusRatio?: number | undefined;
  readonly bonusCap?: number | undefined;
}

interface PifsterBootstrapWindow extends Window {
  readonly PIFSTER_BOOTSTRAP?: {
    readonly nonce?: string;
  } | null;
}

// ---------------------------------------------------------------------------
// Utilities
// ---------------------------------------------------------------------------

function getBootstrapNonce(): string | null {
  const w = window as PifsterBootstrapWindow;
  return w.PIFSTER_BOOTSTRAP?.nonce ?? null;
}

// ---------------------------------------------------------------------------
// URL Parameter Handling
// ---------------------------------------------------------------------------

export function consumeUrlParams(): UrlContext | null {
  const params = new URLSearchParams(window.location.search || '');
  const charityIdRaw = params.get('charity_id');

  if (!charityIdRaw) {
    return null;
  }

  const charityId = Number.parseInt(charityIdRaw, 10);
  if (!Number.isFinite(charityId) || charityId <= 0) {
    console.warn('PIFster: Invalid charity_id in URL, ignoring');
    return null;
  }

  const path = window.location.pathname;
  for (const blocked of BLOCKED_PATHS) {
    if (path.includes(blocked)) {
      return null;
    }
  }

  let periodId: number | null = null;
  const periodIdRaw = params.get('period_id');
  if (periodIdRaw) {
    const parsed = Number.parseInt(periodIdRaw, 10);
    if (Number.isFinite(parsed) && parsed > 0) {
      periodId = parsed;
    } else {
      console.warn('PIFster: Invalid period_id in URL, ignoring');
    }
  }

  let intentToken: string | null = null;
  const intentTokenRaw = params.get('intent_token');
  if (intentTokenRaw) {
    const safeTokenRegex = /^[a-zA-Z0-9_-]+$/;
    if (safeTokenRegex.test(intentTokenRaw) && intentTokenRaw.length <= 256) {
      intentToken = intentTokenRaw;
    } else {
      console.warn('PIFster: Invalid intent_token format in URL, ignoring');
    }
  }

  const context: UrlContext = {
    charityId,
    periodId,
    intentToken,
    action: params.get('action') ?? null,
    entry: params.get('entry') ?? null,
  };

  // Clear params immediately (one-shot)
  try {
    const cleanUrl = new URL(window.location.href);
    for (const key of ['charity_id', 'period_id', 'intent_token', 'action', 'entry']) {
      cleanUrl.searchParams.delete(key);
    }
    window.history.replaceState({}, '', cleanUrl.toString());
  } catch {
    // no-op
  }

  return context;
}

export function getEntryPointFromUrl(urlContext: UrlContext | null): EntryPoint | null {
  if (!urlContext) {
    return null;
  }

  if (urlContext.entry === 'complete_vote') {
    return ENTRY_POINT.COMPLETE_VOTE;
  }

  const action = urlContext.action;
  if (action === 'vote' || action === 'vote_intent' || urlContext.intentToken) {
    return ENTRY_POINT.URL_INTENT;
  }

  return ENTRY_POINT.DONATE_BUTTON;
}

// ---------------------------------------------------------------------------
// API Calls
// ---------------------------------------------------------------------------

export async function mintVoteIntent(
  charityId: number | null,
  periodId: number | null | undefined,
): Promise<MintIntentResponse | null> {
  const apiBase = resolveApiBase();
  if (!apiBase || !charityId) {
    return null;
  }

  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const nonce = getBootstrapNonce();
  if (nonce) {
    headers['X-WP-Nonce'] = nonce;
  }

  const body: Record<string, number> = {
    charity_id: Number.parseInt(String(charityId), 10) || 0,
  };
  if (periodId) {
    body['period_id'] = Number.parseInt(String(periodId), 10);
  }

  try {
    const resp = await fetch(`${apiBase}/vote/mint-intent`, {
      method: 'POST',
      credentials: 'include',
      headers,
      body: JSON.stringify(body),
    });
    if (!resp.ok) {
      return null;
    }
    return (await resp.json()) as MintIntentResponse;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Routing Logic
// ---------------------------------------------------------------------------

export function resolveUserState(eligibilityData: MintIntentResponse | null): UserState {
  const authenticated = eligibilityData !== null || isLoggedIn();
  if (!authenticated) {
    return USER_STATE.LOGGED_OUT;
  }
  if (!eligibilityData) {
    return USER_STATE.INELIGIBLE;
  }
  if (eligibilityData.already_voted) {
    return USER_STATE.ALREADY_VOTED;
  }
  if (eligibilityData.eligible) {
    return USER_STATE.ELIGIBLE;
  }
  return USER_STATE.INELIGIBLE;
}

export function resolveModalMode(userState: UserState, entryPoint: EntryPoint): ModalMode {
  if (userState === USER_STATE.LOGGED_OUT) {
    return MODAL_MODE.MAGIC_LINK;
  }

  if (userState === USER_STATE.ALREADY_VOTED) {
    return MODAL_MODE.DONATE_ONLY;
  }

  if (entryPoint === ENTRY_POINT.DONATE_BUTTON) {
    return MODAL_MODE.DONATE_ONLY;
  }

  // Both eligible and ineligible vote-intent entries use vote_plus_donate.
  // The `eligible` flag in ModalContext tells the FluentCart state machine
  // whether to show VOTE_SUPERCHARGE (eligible) or VOTE_UNLOCK (ineligible).
  return MODAL_MODE.VOTE_PLUS_DONATE;
}

export function buildModalContext(
  baseContext: BaseContext,
  eligibilityData: MintIntentResponse | null,
  entryPoint: EntryPoint,
): ModalContext {
  const userState = resolveUserState(eligibilityData);
  const mode = resolveModalMode(userState, entryPoint);

  return {
    charityId: baseContext.charityId,
    charityName: baseContext.charityName ?? '',
    periodId: eligibilityData?.period_id ?? baseContext.periodId ?? null,
    intentToken: eligibilityData?.intent_token ?? baseContext.intentToken ?? null,
    eligible: eligibilityData?.eligible ?? false,
    alreadyVoted: eligibilityData?.already_voted ?? false,
    votedCharityId: eligibilityData?.voted_charity_id ?? null,
    cumulativeBonusVotes: eligibilityData?.cumulative_bonus_votes ?? 0,
    hasRecurring: eligibilityData?.has_recurring ?? false,
    userState,
    mode,
    entryPoint,
    beneficiary: baseContext.beneficiary ?? null,
    donateHrefFallback: baseContext.donateHrefFallback ?? '',
    bonusRatio: baseContext.bonusRatio,
    bonusCap: baseContext.bonusCap,
  };
}

// ---------------------------------------------------------------------------
// Main Router
// ---------------------------------------------------------------------------

export async function route(
  baseContext: BaseContext,
  entryPoint: EntryPoint,
  callbacks: RouteCallbacks,
): Promise<void> {
  if (!baseContext.charityId) {
    console.warn('[ModalRouter] No charityId provided');
    return;
  }

  const { openDonationModal, openMagicLinkModal, redirectToReturnStatus } = callbacks;

  const eligibilityData = await mintVoteIntent(baseContext.charityId, baseContext.periodId);
  const context = buildModalContext(baseContext, eligibilityData, entryPoint);

  if (entryPoint === ENTRY_POINT.COMPLETE_VOTE && context.userState === USER_STATE.ALREADY_VOTED) {
    if (typeof redirectToReturnStatus === 'function') {
      redirectToReturnStatus(context);
    }
    return;
  }

  if (context.mode === MODAL_MODE.MAGIC_LINK) {
    openMagicLinkModal(context);
  } else {
    await openDonationModal(context);
  }
}

// ---------------------------------------------------------------------------
// window.PIFModalRouter backward-compat assignment
// ---------------------------------------------------------------------------

interface PIFModalRouterGlobal {
  PIFModalRouter?: {
    readonly USER_STATE: typeof USER_STATE;
    readonly MODAL_MODE: typeof MODAL_MODE;
    readonly ENTRY_POINT: typeof ENTRY_POINT;
    readonly isLoggedIn: typeof isLoggedIn;
    readonly consumeUrlParams: typeof consumeUrlParams;
    readonly getEntryPointFromUrl: typeof getEntryPointFromUrl;
    readonly route: typeof route;
    readonly mintVoteIntent: typeof mintVoteIntent;
    readonly resolveUserState: typeof resolveUserState;
    readonly resolveModalMode: typeof resolveModalMode;
    readonly buildModalContext: typeof buildModalContext;
  };
}

(window as unknown as PIFModalRouterGlobal).PIFModalRouter = {
  USER_STATE,
  MODAL_MODE,
  ENTRY_POINT,
  isLoggedIn,
  consumeUrlParams,
  getEntryPointFromUrl,
  route,
  mintVoteIntent,
  resolveUserState,
  resolveModalMode,
  buildModalContext,
};
